import { Alert, Badge, Box, Button, Group, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import type { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useState } from "react";
import { openapi } from "../../../../../openapi";
import type {
  GetAppadminKafkaEmployeeProjectsApiResponse,
  GetAppadminKafkaProjectAccessApiResponse,
} from "../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";

type ProjectAccessRow = GetAppadminKafkaProjectAccessApiResponse["access"][number];
type EmployeeProjectRow = GetAppadminKafkaEmployeeProjectsApiResponse["projects"][number];

const formatDate = (value?: string | Date | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("sv-SE");
};

export const KafkaAdmin: React.FC = () => {
  const [projectNoInput, setProjectNoInput] = useState("");
  const [initialsInput, setInitialsInput] = useState("");
  const [fetchProjectAccess, { data: projectAccessData, error: projectAccessError, isFetching: isFetchingProjectAccess }] =
    openapi.endpoints.getAppadminKafkaProjectAccess.useLazyQuery();
  const [
    fetchEmployeeProjects,
    { data: employeeProjectsData, error: employeeProjectsError, isFetching: isFetchingEmployeeProjects },
  ] = openapi.endpoints.getAppadminKafkaEmployeeProjects.useLazyQuery();

  const projectNo = useMemo(() => {
    const parsed = Number(projectNoInput.trim());
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [projectNoInput]);

  const initials = useMemo(() => {
    const trimmed = initialsInput.trim();
    return trimmed.length > 0 ? trimmed.toLowerCase() : null;
  }, [initialsInput]);

  const searchProjectAccess = () => {
    if (projectNo == null) return;
    fetchProjectAccess({ projectNo });
  };

  const searchEmployeeProjects = () => {
    if (initials == null) return;
    fetchEmployeeProjects({ initials });
  };

  const projectAccessErrorMessage =
    (projectAccessError as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
    (projectAccessError as { message?: string } | undefined)?.message;

  const employeeProjectsErrorMessage =
    (employeeProjectsError as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
    (employeeProjectsError as { message?: string } | undefined)?.message;

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true,
    };
  }, []);

  const projectAccessColumnDefs = useMemo<ColDef<ProjectAccessRow>[]>(
    () => [
      {
        field: "employeeNumber",
        headerName: "Employee no.",
        width: 145,
      },
      {
        headerName: "Initials",
        width: 110,
        valueGetter: (params) => params.data?.employee?.initials ?? "",
      },
      {
        headerName: "Name",
        flex: 1,
        minWidth: 190,
        valueGetter: (params) => params.data?.employee?.name ?? "",
      },
      {
        headerName: "Email",
        flex: 1,
        minWidth: 220,
        valueGetter: (params) => params.data?.employee?.email ?? "",
      },
      {
        headerName: "Mobile",
        width: 135,
        valueGetter: (params) => params.data?.employee?.mobilePhone ?? "",
      },
      {
        headerName: "Location",
        width: 150,
        valueGetter: (params) => params.data?.employee?.postalDistrict ?? "",
      },
      {
        field: "transactionTimestamp",
        headerName: "Access timestamp",
        width: 185,
        valueFormatter: (params) => formatDate(params.value),
        sort: "desc",
      },
    ],
    [],
  );

  const employeeProjectColumnDefs = useMemo<ColDef<EmployeeProjectRow>[]>(
    () => [
      {
        field: "projectNo",
        headerName: "Project no.",
        width: 135,
      },
      {
        headerName: "Job number",
        width: 150,
        valueGetter: (params) => params.data?.project?.jobNumber ?? "",
      },
      {
        headerName: "Name",
        flex: 1,
        minWidth: 240,
        valueGetter: (params) => params.data?.project?.name ?? "",
      },
      {
        headerName: "Status",
        width: 110,
        valueGetter: (params) => params.data?.project?.status ?? "",
      },
      {
        headerName: "Changed",
        width: 165,
        valueGetter: (params) => params.data?.project?.changedDate ?? "",
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        field: "transactionTimestamp",
        headerName: "Access timestamp",
        width: 185,
        valueFormatter: (params) => formatDate(params.value),
        sort: "desc",
      },
    ],
    [],
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Title order={3}>Kafka access lookup</Title>
          <Text size="sm" c="dimmed">
            Search raw Maconomy project data and employee access by project number or initials.
          </Text>
        </Stack>
        {projectAccessData != null && (
          <Badge variant="light" color="green">
            {projectAccessData.access.length} employees
          </Badge>
        )}
      </Group>

      <Paper withBorder p="sm" radius="sm">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <TextInput
              label="Project number"
              placeholder="10242425"
              value={projectNoInput}
              onChange={(event) => setProjectNoInput(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") searchProjectAccess();
              }}
              style={{ flex: "1 1 18rem" }}
            />
            <Button loading={isFetchingProjectAccess} disabled={projectNo == null} onClick={searchProjectAccess}>
              Search project
            </Button>
          </Group>

          {projectAccessErrorMessage != null && (
            <Alert color="red" variant="light">
              {projectAccessErrorMessage}
            </Alert>
          )}

          {projectAccessData != null && (
            <>
              <Group gap="xl" align="flex-start">
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Project
                  </Text>
                  <Text fw={600}>{projectAccessData.project.name}</Text>
                  <Text size="sm" c="dimmed">
                    {projectAccessData.project.projectNo} · {projectAccessData.project.jobNumber}
                  </Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Status
                  </Text>
                  <Text size="sm">{projectAccessData.project.status ?? ""}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Changed
                  </Text>
                  <Text size="sm">{formatDate(projectAccessData.project.changedDate)}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Transaction
                  </Text>
                  <Text size="sm">{formatDate(projectAccessData.project.transactionTimestamp)}</Text>
                </Stack>
              </Group>

              <Box className="ag-theme-quartz" h={420} w="100%">
                <AgGridReact<ProjectAccessRow>
                  rowData={projectAccessData.access}
                  columnDefs={projectAccessColumnDefs}
                  defaultColDef={defaultColDef}
                  loading={isFetchingProjectAccess}
                  animateRows={false}
                  suppressCellFocus
                  suppressContextMenu
                  localeText={AG_GRID_LOCALE_NOR}
                  getRowId={(params) => `${params.data.projectNo}-${params.data.employeeNumber}`}
                  overlayNoRowsTemplate="No employee access rows found for this project."
                />
              </Box>
            </>
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="sm" radius="sm">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <TextInput
              label="Initials"
              placeholder="abc"
              value={initialsInput}
              onChange={(event) => setInitialsInput(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") searchEmployeeProjects();
              }}
              style={{ flex: "1 1 18rem" }}
            />
            <Button loading={isFetchingEmployeeProjects} disabled={initials == null} onClick={searchEmployeeProjects}>
              Search employee
            </Button>
          </Group>

          {employeeProjectsErrorMessage != null && (
            <Alert color="red" variant="light">
              {employeeProjectsErrorMessage}
            </Alert>
          )}

          {employeeProjectsData != null && (
            <>
              <Group gap="xl" align="flex-start">
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Employee
                  </Text>
                  <Text fw={600}>{employeeProjectsData.employees[0]?.name ?? employeeProjectsData.initials}</Text>
                  <Text size="sm" c="dimmed">
                    {employeeProjectsData.initials}
                  </Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Employee no.
                  </Text>
                  <Text size="sm">{employeeProjectsData.employees.map((employee) => employee.employeeNumber).join(", ")}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Email
                  </Text>
                  <Text size="sm">{employeeProjectsData.employees[0]?.email ?? ""}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Projects
                  </Text>
                  <Text size="sm">{employeeProjectsData.projects.length}</Text>
                </Stack>
              </Group>

              <Box className="ag-theme-quartz" h={420} w="100%">
                <AgGridReact<EmployeeProjectRow>
                  rowData={employeeProjectsData.projects}
                  columnDefs={employeeProjectColumnDefs}
                  defaultColDef={defaultColDef}
                  loading={isFetchingEmployeeProjects}
                  animateRows={false}
                  suppressCellFocus
                  suppressContextMenu
                  localeText={AG_GRID_LOCALE_NOR}
                  getRowId={(params) => `${params.data.employeeNumber}-${params.data.projectNo}`}
                  overlayNoRowsTemplate="No project access rows found for this employee."
                />
              </Box>
            </>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};
